import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ActivityLog, LogFilters } from './ActivityLog.types';
import { applyFilters, mapRawToActivityLog } from './ActivityLogSampleData';
import LogFiltersBar from './ActivityLogFilters';
import LogDetailModal from './ActivityLogDetailedModal';
import LogViewSwitcher, { LogViewType } from './LogViewSwitcher';
import ActivityLogCards from './ActivityLogCards';
import ActivityLogTable from './ActivityLogTable';
import ActivityLogTimeline from './ActivityLogTimeline';
import { getLogsTableData } from '@app/api/activityLog.api';
import { notification } from 'antd';
import { get } from '@app/api/http.api';

const ActivityLogPage: React.FC = () => {
  const [viewType, setViewType] = useState<LogViewType>('cards');
  const [filters, setFilters] = useState<LogFilters>({ sortBy: 'timestamp', sortOrder: 'desc' });
  const [page, setPage] = useState(1);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);

  const PAGE_SIZE = 10;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: PAGE_SIZE,
        search: filters.search || undefined,
        module: filters.department || undefined,
        usertype: filters.userType || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        sort_order: filters.sortOrder || 'desc',
      };

      // Call the raw API directly to get unstripped fields
      const { data } = await get<any>('activity_logs', { params }, true);

      const mapped: ActivityLog[] = data.logs.data.map(mapRawToActivityLog);

      setLogs(mapped);
      setTotal(data.logs.total ?? 0);
    } catch (error) {
      console.error('Fetch error:', error);
      notification.error({ message: 'Error', description: 'Failed to fetch activity logs.' });
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (newFilters: LogFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const viewProps = {
    logs,
    total,
    loading,
    page,
    pageSize: PAGE_SIZE,
    onPageChange: setPage,
    onSelect: setSelectedLog,
  };

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e', margin: 0 }}>Activity Log</h2>
          <p style={{ color: '#888', fontSize: 14, margin: '4px 0 0' }}>
            {total} event{total !== 1 ? 's' : ''} found
          </p>
        </div>
        <LogViewSwitcher value={viewType} onChange={setViewType} />
      </div>

      <LogFiltersBar filters={filters} onChange={handleFilterChange} />

      {viewType === 'cards' && <ActivityLogCards {...viewProps} />}
      {viewType === 'table' && <ActivityLogTable {...viewProps} />}
      {viewType === 'timeline' && <ActivityLogTimeline {...viewProps} />}

      <LogDetailModal log={selectedLog} open={!!selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
};

export default ActivityLogPage;
